import {
    DecisionPolicyProfile,
    DecisionPolicyProfileId,
    DecisionScoringWeights,
    DeliveryProvider,
    Driver,
    SystemConfig
} from './types';
import { AVERAGE_MINUTES_PER_KM } from './services/logistics/constants';

export const CLEAN_MAP_STYLES = [
    { elementType: "geometry", stylers: [{ color: "#1e293b" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#1e293b" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#94a3b8" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#334155" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#0f172a" }] }
];

export const SOLAR_MAP_STYLES = [
    { elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#1e293b" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#e2e8f0" }] },
    { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#cbd5e1" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#a5d8ff" }] },
    { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "transit", stylers: [{ visibility: "simplified" }] }
];

export const DAILY_FIXED_COST_DEFAULT = 0;
export const AVERAGE_SHIFT_HOURS_DEFAULT = 8;
export const BASE_MINUTE_COST_CALCULATED = 0;

// v46.2: Standardized Heuristic Constants (Moved to services/logistics/constants.ts)
export const HEURISTIC_SPEED_MULTIPLIER = 1.0; // Can be adjusted by Machine Learning later

export const DEFAULT_DECISION_POLICY_PROFILE: DecisionPolicyProfileId = 'balanced';

export const DECISION_POLICY_PROFILES: Record<DecisionPolicyProfileId, DecisionPolicyProfile> = {
    'sla-first': {
        id: 'sla-first',
        label: 'SLA-first',
        description: 'Prioriza protecao de prazo e antecipa terceirizacao defensiva.',
        scoringWeights: {
            slaPriority: 0.7,
            marginPriority: 0.2,
            capacityPriority: 0.1
        }
    },
    balanced: {
        id: 'balanced',
        label: 'Balanced',
        description: 'Equilibrio entre SLA, margem e preservacao operacional.',
        scoringWeights: {
            slaPriority: 0.5,
            marginPriority: 0.3,
            capacityPriority: 0.2
        }
    },
    'margin-first': {
        id: 'margin-first',
        label: 'Margin-first',
        description: 'Foco maior em margem, mantendo protecoes de SLA essenciais.',
        scoringWeights: {
            slaPriority: 0.25,
            marginPriority: 0.6,
            capacityPriority: 0.15
        }
    }
};

export const getDecisionPolicyWeights = (profileId?: DecisionPolicyProfileId): DecisionScoringWeights => {
    const resolvedProfileId = profileId ?? DEFAULT_DECISION_POLICY_PROFILE;
    return DECISION_POLICY_PROFILES[resolvedProfileId]?.scoringWeights
        ?? DECISION_POLICY_PROFILES[DEFAULT_DECISION_POLICY_PROFILE].scoringWeights;
};

export const DEFAULT_CONFIG: SystemConfig = {
    storeName: 'Minha Nova Operação',
    businessAddress: '',
    businessLat: -23.55052,
    businessLng: -46.63330,
    maxDeliveryRadiusKm: 10,
    googleMapsApiKey: '',
    streetViewHeading: 235,
    streetViewRadius: 30,
    operatingHours: {},
    averageKitchenTime: 10,
    driversCollectPayments: false,
    shopWhatsApp: '',
    rainRiskPercentage: 10,
    rainSurchargeLight: 10,
    rainSurchargeHeavy: 20,
    trafficRiskPercentage: 10,
    dailyFixedCost: DAILY_FIXED_COST_DEFAULT,
    averageShiftHours: AVERAGE_SHIFT_HOURS_DEFAULT,
    driverFixedFeePerOrder: 0,
    costPerMinuteLowDemand: 0,
    costPerMinuteHighDemand: 0,
    loadFactorThreshold: 6.0,
    fleetConfig: {
        maxOrdersPerRoute: 6,
        slaToleranceMinutes: 15,
        clusteringMaxRadiusKm: 3,
        clusteringMaxAngleDegrees: 90,
        clusteringMinDistanceForAngleCheck: 0.7,
        zigzagIgnoreTimeMinutes: 4,
        serviceTimeMinutes: 5,
    },
    thirdPartyConfig: {
        maxOrdersPerRoute: 2,
        slaToleranceMinutes: 7,
        clusteringMaxRadiusKm: 10,
        clusteringMaxAngleDegrees: 50,
        clusteringMinDistanceForAngleCheck: 0.8,
        zigzagIgnoreTimeMinutes: 3,
        serviceTimeMinutes: 7,
    },
    thirdPartyDeviationThreshold: 1.4,
    thirdPartySmartRouteDiscount: 15,
    thirdPartyMinSavings: 0.50,
    enableThirdPartyDeviation: true,
    enableThirdPartySmartRouteDiscount: true,
    enableThirdPartyMinSavings: true,
    oracle: {
        active: false,
        averageDailyOrders: 0,
        isManualOverride: false,
        hourlyShare: new Array(24).fill(0),
        dailyWeights: {},
        preservationMode: 'proportional',
        maxPreservationBonus: 5,
        manualPreservationLevel: 0,
        decisionPolicyProfile: DEFAULT_DECISION_POLICY_PROFILE,
        decisionScoringWeights: getDecisionPolicyWeights(DEFAULT_DECISION_POLICY_PROFILE)
    },
    // v37.2: Defaults de Integração Blindados
    cardapioWeb: {
        active: false,
        proxyBaseUrl: '',
        merchantId: '',
        apiToken: '',
        pollingIntervalSeconds: 30,
        scheduledRoutingLeadMinutes: 45,
        enableGhostDispatch: false,
        ghostDispatchMode: 'predictive'
    },
    openDelivery: {
        active: false,
        mode: 'simulation',
        clientId: '',
        clientSecret: '',
        baseUrl: '',
        callMode: 'delayed'
    },
    communication: {
        active: false,
        enableAutoHailing: false,
        hailingPersonalityPrompt: '',
        audioClips: [],
        textClips: [
            { id: 'def_normal', scenario: 'Chegada Normal', content: "Olá, [Nome]! Motoboy da [Nome da Loja] aqui. Chego em cerca de [Tempo]." },
            { id: 'def_atraso', scenario: 'Atraso', content: "Olá, [Nome]. Sou o motoboy da [Nome da Loja]. Tive um imprevisto e devo atrasar um pouco, peço desculpas." }
        ],
        scenarioPreferences: {},
        sofiaVoiceConfig: {
            liveVoiceName: 'Kore',
            ttsVoiceName: 'Kore',
            proactiveQuality: 'native-tts',
            selectedVoiceQuality: 'local',
            budgetGuardVoiceFallback: true
        }
    },
    sofiaPersonalityPrompt: `Você é a Sofia, a Inteligência Operacional do S.A.L. Rota. Sua missão é atuar como o braço direito do despachante, processando em tempo real todos os dados de logística para garantir que cada pedido chegue ao cliente no menor tempo e com o menor custo possível.

Seu tom de voz deve ser:
- Profissional e Simpático: Trate o despachante como um parceiro de equipe.
- Objetivo e Claro: Vá direto ao ponto, mas sem ser ríspida.
- Analítico: Use os dados técnicos (SLA, custos, distância) para sustentar suas sugestões.

MANUAL DE ESTRATÉGIAS DIVERGENTES:
1. Retenção de Produção (WAITING_KITCHEN_FOR_BATCH): O sistema sugere "ESPERAR" na cozinha quando um pedido tem muito Slack (tempo de folga). Isso NÃO é um erro; é "Paciência Estratégica" para esperar um par logístico e economizar uma perna de entrega.
2. Despacho Retido (RETENTION_ACTIVE): Seguramos pedidos prontos no balcão porque há outros pedidos quase saindo da cozinha para a mesma região. O objetivo é maximizar o agrupamento (batching).
3. Bumerangue (BOOMERANG_OPPORTUNITY): O motoboy faz uma entrega próxima e volta rápido para pegar o restante da rota. Isso mantém a frota girando sem comprometer o SLA.
4. Preservação de Capacidade (MARGIN_SHEDDING): O sistema detectou um Pico de Demanda iminente e forçou a terceirização de uma rota longa ('Arriscada') porque a taxa do cliente cobria o custo do App. Isso preserva a frota interna livre para o pico.

Quando o usuário perguntar "O que eu faço?", você deve realizar uma SÍNTESE DO CENÁRIO atual seguindo este protocolo:
1. PANORAMA: Resuma o estado da operação (Clima, % de ocupação da frota, total de pedidos aguardando).
2. AÇÕES IMEDIATAS: Liste quais rotas ou pedidos devem ser despachados imediatamente (priorizando frota própria).
3. ESTRATÉGIA: Explique as retenções ou bumerangues ativos como decisões inteligentes de economia, usando o "Manual de Estratégias Divergentes".
4. ALERTA: Aponte qualquer risco de real de atraso (pedidos onde o Slack é crítico ou negativo).

DIRETRIZES TÉCNICAS:
- Priorize Frota Própria para diluir custos fixos.
- Use Terceirização (Apps) apenas se a frota estiver ocupada ou se a economia for real.
- Valorize Agrupamentos (Batching) para reduzir o custo por entrega.
- NUNCA trate um pedido em "Retenção" como um problema de atraso se o sistema sugeriu essa espera.`,
    autoPilotEnabled: false,
    isEconomicMode: false,
    isSolarMode: false,
    // API de Roteamento: OSRM (gratuita) como padrão com fallback opcional
    routingApiProvider: 'osrm',
    routingApiFallbackEnabled: true,
    // Estrategia de recomendacao padrao: preserva o algoritmo avancado existente.
    routingRecommendationMode: 'advanced',
    routingOsrmCalibration: {
        enabled: true,
        shortTripMaxDistanceKm: 3,
        mediumTripMaxDistanceKm: 8,
        shortTripMultiplier: 2.3,
        mediumTripMultiplier: 1.4,
        longTripMultiplier: 1.3,
        fixedDelayMinutes: 1,
        peakHourEnabled: true,
        peakHourMultiplier: 1.1,
        peakMorningStartHour: 7,
        peakMorningEndHour: 9,
        peakEveningStartHour: 17,
        peakEveningEndHour: 19
    }
};

export const DEFAULT_PROVIDERS: DeliveryProvider[] = [
    {
        id: 'motoboy_proprio',
        name: 'Frota Própria (Tabela)',
        active: true,
        priority: 1,
        strategy: 'tiered',
        baseFee: 0,
        tiers: []
    }
];

export const DEFAULT_DRIVERS: Driver[] = [];

// --- CENTRALIZED APPLICATION CONSTANTS ---
// SINGLE SOURCE OF TRUTH for all static parameters across the app

// 1. Timing Intervals
export const TIMING = {
  // Recalculation intervals
  RECALC_INTERVAL_MS: 15000,           // Main intelligence recalculation (15s)
  NEXT_UPDATE_BUFFER_MS: 500,          // Buffer before next update (0.5s)
  GHOST_DISPATCH_INTERVAL_MS: 15000,   // Ghost dispatch check (15s)
  TIME_TICK_INTERVAL_MS: 60000,        // UI time ticker (1min)
  
  // Scheduled routing
  DEFAULT_SCHEDULED_LEAD_MINUTES: 45,  // Lead time for scheduled orders
  
  // Date calculations
  MS_PER_MINUTE: 60000,
  MS_PER_DAY: 24 * 60 * 60 * 1000,   // 24 hours in ms
} as const;

// 2. Kitchen & Prep Time
export const KITCHEN = {
  DEFAULT_AVERAGE_PREP_TIME_MIN: 25,   // Default kitchen prep time
  DEFAULT_ROLLING_WINDOW_SIZE: 3,     // Rolling window for prep time averaging
  DEFAULT_KITCHEN_BUFFER_MIN: 2,       // Buffer added to retention threshold
  DEFAULT_HOLD_MULTIPLIER: 1.5,       // Multiplier for max hold time
  DEFAULT_TIME_LIMIT_MIN: 60,          // Default order time limit
} as const;

// 3. Data Limits
export const LIMITS = {
  // Order limits
  DEFAULT_ORDERS_QUERY_LIMIT: 200,     // Max orders loaded from DB
  DEFAULT_MAX_HOLD_ORDERS: 2,          // Max orders to hold simultaneously
  DEFAULT_MIN_HOLD_MINUTES: 6,          // Minimum hold time before release
  
  // Scoring
  MIN_SAMPLES_FOR_ROLLING_AVG: 3,     // Min samples to form rolling average
  
  // Queue
  HIGH_QUEUE_THRESHOLD: 10,            // Queue size considered "high"
} as const;

// 4. Scoring Weights & Thresholds
export const SCORING = {
  // Fleet bias
  IDLE_FLEET_LOAD_THRESHOLD: 0.5,     // Load factor considered "idle"
  IDLE_FLEET_BIAS_VALUE: 1.0,          // $1.00 tolerance when fleet idle
  
  // Deviation threshold
  DEFAULT_DEVIATION_THRESHOLD: 1.4,      // Third-party deviation multiplier
  DEFAULT_MIN_SAVINGS: 0,               // Minimum savings threshold
  
  // Arbitrations
  BOOMERANG_TIEBREAKER: 0.02,         // Boomerang priority tiebreaker
  DISPATCH_HOLD_TIEBREAKER: 0.01,       // Dispatch hold priority tiebreaker
  
  // Own fleet tiebreaker
  OWN_FLEET_TIEBREAKER: 0.02,
  THIRD_PARTY_TIEBREAKER: 0.03,
  
  // SLA scoring
  SLA_SCORING_BUFFER_MIN: 10,          // Buffer for SLA scoring
  SLA_SCORING_NORMALIZER: 24,           // Normalizer for SLA scores
  SLA_SCORING_NORMALIZER_TIGHT: 22,     // Tighter normalizer for tight SLAs
  
  // Margin scoring
  MARGIN_BASELINE: 0.5,                // Baseline margin score
  MARGIN_WEIGHT_FACTOR: 2,              // Weight factor for margin spread
  
  // Capacity scoring
  CAPACITY_BASELINE_LOW: 0.1,          // Low capacity score
  CAPACITY_BASELINE_MED: 0.5,          // Medium capacity score
  CAPACITY_BASELINE_HIGH: 0.6,          // High capacity score
  CAPACITY_BASELINE_VERY_HIGH: 0.75,     // Very high capacity score
  OVERTIME_APPROVAL_PENALTY: 0.45,      // Penalty for overtime approval
  
  // Delay impact
  DISPATCH_HOLD_DELAY_IMPACT: 8,       // Delay impact for dispatch hold
  PRODUCTION_RETENTION_DELAY: 5,         // Delay for production retention
  DEFAULT_RETENTION_DELAY: 5,            // Default retention delay
} as const;

// 5. Distance Thresholds
export const DISTANCE = {
  // Delivery/pickup
  MAX_DELIVERY_RADIUS_KM: 10,          // Max delivery radius from store
  MAX_DELIVERY_DISTANCE_THRESHOLD_KM: 0.25, // Threshold for geofence check
  
  // Clustering
  DEFAULT_CLUSTER_RADIUS_KM: 3,         // Default clustering radius
  DEFAULT_ANGLE_DEGREES: 90,             // Default max angle for clustering
  DEFAULT_MIN_ANGLE_DISTANCE_KM: 0.7,    // Min distance for angle check
  
  // Third-party specific
  THIRD_PARTY_CLUSTER_RADIUS_KM: 10,    // Third-party clustering radius
  THIRD_PARTY_ANGLE_DEGREES: 50,       // Third-party max angle
  THIRD_PARTY_MIN_ANGLE_DISTANCE_KM: 0.8,// Third-party min distance for angle
  
  // Routing
  SHORT_TRIP_MAX_KM: 3,                // Short trip threshold
  MEDIUM_TRIP_MAX_KM: 8,               // Medium trip threshold
} as const;

// 6. Time Limits (Minutes)
export const TIME_LIMITS = {
  // SLA tolerances
  DEFAULT_SLA_TOLERANCE_OWN: 10,       // Own fleet SLA tolerance
  DEFAULT_SLA_TOLERANCE_THIRD: 5,       // Third-party SLA tolerance
  
  // Service times
  DEFAULT_SERVICE_TIME_OWN: 3,           // Service time at door (own)
  DEFAULT_SERVICE_TIME_THIRD: 4,         // Service time at door (third)
  
  // Zigzag ignore
  DEFAULT_ZIGZAG_IGNORE_MIN: 4,        // Ignore zigzag under this time
  
  // Draining
  DEFAULT_DRAINAGE_THRESHOLD_MIN: 25,    // Drainage threshold
  
  // Oracle lookups
  DEFAULT_LOOKAHEAD_HOURS_MIN: 3,       // Minimum lookahead hours
  DEFAULT_LOOKAHEAD_HOURS_MAX: 6,       // Maximum lookahead hours
  
  // Surge
  DEFAULT_SURGE_MULTIPLIER_MIN: 7,      // Minimum surge multiplier
  DEFAULT_SURGE_MULTIPLIER_MAX: 14,     // Maximum surge multiplier
  
  // Peak threshold factors
  PEAK_THRESHOLD_FACTOR_MIN: 0.68,       // Min peak threshold
  PEAK_THRESHOLD_FACTOR_MAX: 0.92,       // Max peak threshold
  PEAK_THRESHOLD_FLOOR: 0.04,            // Floor for flat days (4%)
  
  // Time scoring
  TIME_SCORING_BUFFER_LOW: 6,          // Low buffer for time scoring
  TIME_SCORING_BUFFER_MED: 8,           // Medium buffer for time scoring
  TIME_SCORING_BUFFER_HIGH: 10,          // High buffer for time scoring
} as const;
